import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { RESUME } from '@/components/landing/cabinet/data'
import { NAV_LINKS } from '@/constants/site'

const INITIALS = RESUME.name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase()

const PIXEL = 'font-[family-name:var(--font-silkscreen,monospace)] uppercase'

/* The cabinet's marquee: a dark strip with the name on the left and the
 * pages as a row of links on the right. Fixed and 56px tall (52px on
 * phones), which the home cabinet's --cab-top already clears. Styled with
 * utilities only, so it never depends on the .arc stylesheet block. */
export const SiteHeader: FC = (): ReactNode => {
    const { pathname } = useRouter()

    return (
        <header className='fixed inset-x-0 top-0 z-50 h-[52px] bg-[#26323b] shadow-[0_3px_0_#1d272e] md:h-14'>
            <div className='mx-auto flex h-full max-w-[1240px] items-center justify-between gap-4 px-5 md:px-12'>
                <Link
                    href='/'
                    aria-label={`${RESUME.name}, home`}
                    className='group flex min-w-0 items-center gap-3 text-[#f3ebcf] focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-dashed focus-visible:outline-[#ecca8c]'
                >
                    <span
                        aria-hidden
                        className={cn(
                            PIXEL,
                            'grid size-[34px] flex-none place-items-center bg-[#357471] text-[13px] font-bold text-[#fbf4dc] shadow-[inset_0_0_0_3px_#574349,3px_3px_0_#e0896a]'
                        )}
                    >
                        {INITIALS}
                    </span>
                    <span className={cn(PIXEL, 'hidden truncate text-[13px] tracking-[0.08em] group-hover:text-[#ecca8c] sm:inline')}>
                        {RESUME.name}
                    </span>
                </Link>

                <nav aria-label="Main">
                    <ul className='m-0 flex list-none items-center gap-5 p-0 sm:gap-8'>
                        {NAV_LINKS.map((link) => {
                            const current = pathname === link.href
                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={current ? 'page' : undefined}
                                        className={cn(
                                            PIXEL,
                                            'relative inline-flex min-h-10 items-center text-[12px] tracking-[0.08em] no-underline transition-colors sm:text-[13px]',
                                            'after:absolute after:inset-x-0 after:bottom-1.5 after:h-[3px] after:origin-left after:bg-[#ecca8c] after:transition-transform after:duration-150',
                                            'focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-dashed focus-visible:outline-[#ecca8c]',
                                            current
                                                ? 'text-[#ecca8c] after:scale-x-100'
                                                : 'text-[#f3ebcf] after:scale-x-0 hover:text-[#ecca8c] hover:after:scale-x-100'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>
            </div>
        </header>
    )
}
